import React, { useState, useEffect } from 'react';
import { Box, Typography, Container, Grid, CircularProgress, Chip } from '@mui/material';
import PageBanner from '../../components/PageBanner';
import SEO from '../../components/SEO';
import SectionSidebar from '../../components/SectionSidebar';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TempleHinduIcon from '@mui/icons-material/TempleHindu';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import StarIcon from '@mui/icons-material/Star';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';

const GOLD = '#d4af37';

interface DeitiesContent {
  title: string;
  description: string;
  principalDeity: string;
  principalDescription: string;
  presidingDeity: string;
  presidingDescription: string;
  otherDeities: string;
  worshipSignificance: string;
}

const defaultContent: DeitiesContent = {
  title: 'Deities',
  description: 'Learn about the divine forms worshipped in our temple',
  principalDeity: 'Sri Bhagavathy (Devi)',
  principalDescription: 'The presiding deity of Muchukunnu Sri Kotta-Kovilakam Kshethram is Sri Bhagavathy, a powerful manifestation of the Divine Mother. Worshipped with daily Chandana-Charthu and Pushpanjali, the goddess is believed to guard the welfare and prosperity of devotees across the region. Special sevas are performed every Friday, drawing large gatherings of the faithful. The sanctum\'s murti is adorned with gold ornaments during annual festivals, radiating an aura of divine grace.',
  presidingDeity: 'Lord Shiva (Mahadeva)',
  presidingDescription: 'Lord Shiva occupies the sub-shrine to the north of the main sanctum. Daily Rudra Abhishekam and Dhara are performed with milk, honey, and sacred ash. Mondays are especially auspicious, with extended pujas and Bilva-archana attracting hundreds of devotees. The Shiva Lingam here is considered Swayambhu, and its consecration predates written records of the temple.',
  otherDeities: 'The temple complex also enshrines Lord Ganapathy, who is invoked before every ritual, Sri Subrahmanya with a dedicated Skanda-Shashti observance, Naga Devatha with annual Sarpa Bali and Noorum Palum rituals, and Lord Ayyappa with a Mandala Pooja during the Sabarimala season. Each sub-shrine has its own festival calendar and special vazhipad options for devotees.',
  worshipSignificance: 'The synergy of multiple deities in a single complex is considered rare and highly auspicious. Devotees believe that a single pradakshina (circumambulation) here earns the spiritual merit of visiting multiple temples. The temple\'s tantric traditions, maintained by an unbroken lineage of Melsanthi families, ensure rituals follow the authentic Thanthra-Sastra prescriptions passed down for generations.'
};

const deityCards = [
  { name: 'Sri Bhagavathy', role: 'Principal Deity • Divine Mother', gradient: ['#f59e0b', '#d97706'] as [string, string], initial: 'B', sub: 'Daily Chandana-Charthu • Special Friday Sevas' },
  { name: 'Lord Shiva', role: 'Mahadeva • Swayambhu Lingam', gradient: ['#8b5cf6', '#7c3aed'] as [string, string], initial: 'S', sub: 'Daily Abhishekam • Monday Bilva-archana' },
  { name: 'Lord Ganapathy', role: 'Remover of Obstacles', gradient: ['#ef4444', '#dc2626'] as [string, string], initial: 'G', sub: 'Invoked at the start of every ritual' },
  { name: 'Sri Subrahmanya', role: 'Divine Commander', gradient: ['#3b82f6', '#2563eb'] as [string, string], initial: 'M', sub: 'Skanda-Shashti special observance' },
  { name: 'Naga Devatha', role: 'Serpent Deity', gradient: ['#22c55e', '#16a34a'] as [string, string], initial: 'N', sub: 'Annual Sarpa Bali & Noorum Palum' },
  { name: 'Lord Ayyappa', role: 'Dharma Sastha', gradient: ['#f97316', '#ea580c'] as [string, string], initial: 'A', sub: 'Mandala Pooja during Sabarimala season' },
];

export default function Deities() {
  const [content, setContent] = useState<DeitiesContent>(defaultContent);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'about_content', 'deities'), (docSnap) => {
      if (docSnap.exists()) {
        setContent(docSnap.data() as DeitiesContent);
      }
      setLoading(false);
    }, (error) => {
      console.error('Error loading deities:', error);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f8fafb', minHeight: '100vh' }}>
      <SEO title="Deities — Kottayil Temple | പ്രതിഷ്ടകൾ" description="Presiding deities of Kottayil Temple — Sree Bhadrakali and other divine deities worshipped at Muchukunnu Sri Kotta-Kovilakam Kshethram, Kozhikode, Kerala." path="/about/deities" keywords="Kottayil temple deities, Sree Bhadrakali, Muchukunnu deities, Bhadrakali temple Kerala, ഭദ്രകാളി, കൊട്ടയിൽ ക്ഷേത്രം പ്രതിഷ്ട, ദേവതകൾ, temple gods Kerala" />
      <PageBanner title={content.title} />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>

            {/* HERO */}
            <Box sx={{
              borderRadius: '28px', overflow: 'hidden', mb: 4,
              boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #78350f 0%, #d97706 40%, #fbbf24 100%)',
              position: 'relative',
            }}>
              <Box sx={{ position: 'absolute', top: -50, right: -50, width: 220, height: 220, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', bottom: -30, left: -30, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

              <Box sx={{ px: { xs: 3, sm: 4 }, py: { xs: 4, sm: 5 }, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2.5 }}>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '20px',
                    background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '2px solid rgba(255,255,255,0.2)', boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
                  }}>
                    <AutoAwesomeIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.6rem', sm: '2rem' }, color: '#fff', lineHeight: 1.15, mb: 0.5, textShadow: '0 2px 12px rgba(0,0,0,0.2)' }}>
                      {content.title}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '0.82rem', sm: '0.9rem' }, color: 'rgba(255,255,255,0.65)', fontWeight: 500 }}>
                      {content.description}
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: { xs: '0.88rem', sm: '0.95rem' }, color: 'rgba(255,255,255,0.55)', lineHeight: 1.9, maxWidth: 560, mb: 2.5 }}>
                  The temple enshrines multiple divine presences, each with dedicated rituals and festivals. Discover the deities and their spiritual significance.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Chip icon={<TempleHinduIcon sx={{ fontSize: '16px !important', color: '#d97706 !important' }} />} label="Sacred Shrines" sx={{ bgcolor: 'rgba(255,255,255,0.95)', color: '#78350f', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', '& .MuiChip-icon': { ml: '8px' } }} />
                  <Chip icon={<AutoAwesomeIcon sx={{ fontSize: '16px !important', color: '#fcd34d !important' }} />} label="Divine Presences" sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.9)', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', border: '1px solid rgba(255,255,255,0.15)', '& .MuiChip-icon': { ml: '8px' } }} />
                </Box>
              </Box>
            </Box>

            {/* DEITY GRID */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {deityCards.map((d) => {
                const [gS, gE] = d.gradient;
                return (
                  <Grid item xs={12} sm={6} key={d.name}>
                    <Box sx={{
                      bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden',
                      border: '1px solid rgba(0,0,0,0.06)',
                      boxShadow: '0 4px 24px rgba(0,0,0,0.04)', height: '100%',
                      transition: 'all 400ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                      '&:hover': { transform: 'translateY(-5px)', boxShadow: `0 20px 60px rgba(0,0,0,0.08), 0 0 0 1px ${gS}15` },
                    }}>
                      <Box sx={{ background: `linear-gradient(135deg, ${gS}, ${gE})`, px: 2.5, py: 1.8, position: 'relative', overflow: 'hidden' }}>
                        <Box sx={{ position: 'absolute', top: -15, right: -15, width: 60, height: 60, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 2 }}>
                          <Box sx={{ width: 40, height: 40, borderRadius: '14px', background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.25)' }}>
                            <Typography sx={{ fontSize: '0.9rem', fontWeight: 900, color: '#fff' }}>{d.initial}</Typography>
                          </Box>
                          <Box>
                            <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', color: '#fff' }}>{d.name}</Typography>
                            <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>{d.role}</Typography>
                          </Box>
                        </Box>
                      </Box>
                      <Box sx={{ px: 2.5, py: 1.8 }}>
                        <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', lineHeight: 1.7 }}>{d.sub}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>

            {/* DETAIL CARDS */}
            {[
              { title: content.principalDeity, text: content.principalDescription, icon: TempleHinduIcon, gradient: ['#f59e0b', '#d97706'] },
              { title: content.presidingDeity, text: content.presidingDescription, icon: LocalFireDepartmentIcon, gradient: ['#8b5cf6', '#7c3aed'] },
              { title: 'Other Deities & Sub-Shrines', text: content.otherDeities, icon: StarIcon, gradient: ['#22c55e', '#16a34a'] },
            ].map((card) => {
              const CIcon = card.icon;
              return (
                <Box key={card.title} sx={{ bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 4px 24px rgba(0,0,0,0.04)', mb: 2.5 }}>
                  <Box sx={{ background: `linear-gradient(135deg, ${card.gradient[0]}, ${card.gradient[1]})`, px: 2.5, py: 2, position: 'relative', overflow: 'hidden' }}>
                    <Box sx={{ position: 'absolute', top: -15, right: -15, width: 60, height: 60, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 2 }}>
                      <Box sx={{ width: 42, height: 42, borderRadius: '14px', background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.25)' }}>
                        <CIcon sx={{ fontSize: 22, color: '#fff' }} />
                      </Box>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.92rem', color: '#fff', textShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>{card.title}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ px: { xs: 2.5, sm: 3 }, py: 2.5 }}>
                    <Typography sx={{ fontSize: '0.84rem', color: '#64748b', lineHeight: 2, textAlign: 'justify' }}>{card.text}</Typography>
                  </Box>
                </Box>
              );
            })}

            {/* SIGNIFICANCE FOOTER */}
            <Box sx={{
              background: 'linear-gradient(145deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
              borderRadius: '24px', p: { xs: 3, sm: 3.5 }, position: 'relative', overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.05)', boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <Box sx={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: `radial-gradient(circle, ${GOLD}12 0%, transparent 70%)` }} />
              <Box sx={{ position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '12px', background: `linear-gradient(135deg, ${GOLD}, #b8941f)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 6px 20px ${GOLD}30` }}>
                    <SelfImprovementIcon sx={{ fontSize: 20, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#fff', lineHeight: 1.2 }}>Spiritual Significance</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', fontWeight: 500 }}>Sacred blessings of the divine presences</Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.45)', lineHeight: 1.9 }}>
                  {content.worshipSignificance}
                </Typography>
              </Box>
            </Box>

          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="about" activePath="/about/deities" />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
